import { useState, useEffect, useRef } from 'react';
import { invoke } from '../../tauriBridge';
import { useInstanceStore } from '../../stores/instanceStore';

export default function ServerInfo() {
  const serverUrl = useInstanceStore((s) => s.serverUrl);
  const setServerUrl = useInstanceStore((s) => s.setServerUrl);
  const activeInstance = useInstanceStore((s) => s.activeInstance());

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(serverUrl);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse server URL into host and port
  const parseUrl = (url: string) => {
    try {
      const u = new URL(url);
      return { host: u.hostname, port: u.port || (u.protocol === 'https:' ? '443' : '80') };
    } catch {
      return { host: url, port: '?' };
    }
  };

  const { host: serverHost, port: apiPort } = parseUrl(serverUrl);

  // Check server connectivity
  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        await invoke('get_server_status');
        if (alive) setConnected(true);
      } catch {
        if (alive) setConnected(false);
      }
    };
    check();
    const interval = setInterval(check, 5000);
    return () => { alive = false; clearInterval(interval); };
  }, [serverUrl]);

  // Sync persisted serverUrl to Tauri backend on mount
  useEffect(() => {
    invoke('set_server_url', { url: serverUrl }).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === serverUrl) {
      setEditing(false);
      setDraft(serverUrl);
      return;
    }
    setError(null);
    try {
      await setServerUrl(trimmed);
      setEditing(false);
    } catch (e) {
      setError(String(e));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
    if (e.key === 'Escape') { setEditing(false); setDraft(serverUrl); }
  };

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const nmeaHost = serverHost;
  const nmeaPort = activeInstance?.tcp_port ?? 10110;

  return (
    <div className="flex items-center space-x-3 text-xs">
      {/* Server connection indicator + editable URL */}
      <div className="flex items-center space-x-1.5">
        <span className={`status-dot ${connected ? 'status-connected' : 'status-error'}`} />
        {editing ? (
          <input
            ref={inputRef}
            className="bg-nautical-darker border border-nautical-border rounded px-1.5 py-0.5 text-xs text-gray-200 w-52 outline-none focus:border-blue-500"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSubmit}
          />
        ) : (
          <button
            className="text-gray-400 hover:text-gray-200 transition-colors"
            onClick={() => { setDraft(serverUrl); setEditing(true); }}
            title={`Server: ${serverUrl}\nClick to change`}
          >
            <span className="text-gray-500">Server:</span>{' '}
            <span className="text-gray-300">{serverHost}:{apiPort}</span>
          </button>
        )}
      </div>

      {/* Separator */}
      <span className="text-gray-600">|</span>

      {/* NMEA TX address */}
      <div className="flex items-center space-x-1" title="NMEA TCP transmission address for the active instance">
        <span className="text-gray-500">NMEA TX:</span>
        <span className="text-cyan-400 font-mono">{nmeaHost}:{nmeaPort}</span>
      </div>

      {error && <span className="text-red-400">{error}</span>}
    </div>
  );
}
